import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CornerDownLeft, TextCursorInput } from 'lucide-react'
import { useCurrentCycleId } from './useQuickFilters'
import { useFilterLabels } from './useFilterLabels'
import type { FilterFieldKey, TaskFilter } from '@/lib/types/filter'
import type { FilterTargetGroup } from '@/store/ProjectFilterStore'
import type { FilterQueryContext, ParsedFilterToken } from '@/lib/filters/parseFilterQuery'
import { looksLikeFilterQuery, parseFilterQuery } from '@/lib/filters/parseFilterQuery'
import { CommandGroup, CommandItem } from '@/components/ui/command'
import { useCycles, useMembers, useProjects } from '@/lib/db/rxdb/hooks'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { useAllLabels } from '@/lib/hooks/useLabels'
import useLoginStore from '@/store/LoginStore'
import useProjectFilterStore from '@/store/ProjectFilterStore'
import { addToNewGroup, isGrouped, upsertFilters } from '@/lib/filters/filterGroups'

/** Marks typed-filter items so a filtering cmdk list always keeps them. */
export const TYPED_FILTER_ITEM_PREFIX = 'typed-filter:'

interface TypedFilterOptions {
  projectId: string
  scope: 'project' | 'space'
  /** Properties this view can't take from typed input (e.g. project on a project page). */
  lockedFields?: Array<FilterFieldKey>
  /**
   * Where a typed query without `or` goes: this OR group, a new one, or
   * (undefined) the first group when the view already has groups.
   */
  targetGroup?: FilterTargetGroup
}

/**
 * Parses what's typed into the Filter menu or ⌘K as filter syntax
 * (design 9b) against this workspace's statuses, people, labels and cycles.
 */
export function useTypedFilter(
  text: string,
  { projectId, scope, lockedFields, targetGroup }: TypedFilterOptions,
) {
  const workspaceId = useLoginStore((s) => s.workspaceId)
  const userId = useLoginStore((s) => s.userId)
  const {
    all: { statuses },
    open: { statuses: openStatuses },
  } = useStatuses()
  const { members } = useMembers()
  const { labels } = useAllLabels(workspaceId)
  const { cycles } = useCycles()
  const { projects } = useProjects({ isDeleted: false })
  const currentCycleId = useCurrentCycleId(projectId, scope)
  const current = useProjectFilterStore((s) => s.filters[projectId])
  const loadFilters = useProjectFilterStore((s) => s.loadSavedFilter)
  const replaceUnlocked = useProjectFilterStore((s) => s.replaceUnlockedFilters)

  const isTyped = looksLikeFilterQuery(text)

  const context = useMemo<FilterQueryContext>(() => {
    const openIds = new Set(openStatuses.map((s) => s.id))
    return {
      statuses: statuses.map((s) => ({ id: s.id, name: s.name, isOpen: openIds.has(s.id) })),
      members: members.map((m) => ({ userId: m.userId, name: m.name ?? m.email ?? '' })),
      labels: labels.map((l) => ({ id: l.id, name: l.name })),
      cycles: cycles.filter((c) => !c.isDeleted).map((c) => ({ id: c.id, name: c.name })),
      projects: projects.map((p) => ({ id: p.id, name: p.name })),
      currentUserId: userId,
      currentCycleId: currentCycleId ?? null,
    }
  }, [statuses, openStatuses, members, labels, cycles, projects, userId, currentCycleId])

  const parsed = useMemo(() => {
    if (!isTyped) return null
    const result = parseFilterQuery(text, context)
    const locked = new Set(lockedFields ?? [])
    // A token for a locked property reads fine but can't be applied here.
    const tokens: Array<ParsedFilterToken> = result.tokens.map((token) =>
      token.ok && locked.has(token.filter.fieldKey)
        ? { raw: token.raw, ok: false, error: 'unsupported' }
        : token,
    )
    return { tokens, filters: result.filters.filter((f) => !locked.has(f.fieldKey)) }
  }, [isTyped, text, context, lockedFields])

  const apply = useCallback(
    (filters: Array<TaskFilter>) => {
      const locked = lockedFields ?? []
      // Typed `or`: the query is the whole expression.
      if (isGrouped(filters)) {
        replaceUnlocked(projectId, filters, locked)
        return
      }
      const existing = current ?? []
      if (targetGroup === 'new') {
        const [first, ...rest] = filters
        if (!first) return
        const withFirst = addToNewGroup(existing, first, locked)
        const group = withFirst.at(-1)?.group
        loadFilters(projectId, upsertFilters(withFirst, rest.map((f) => ({ ...f, group }))))
        return
      }
      const group = targetGroup ?? (isGrouped(existing) ? 0 : undefined)
      loadFilters(projectId, upsertFilters(existing, filters.map((f) => ({ ...f, group }))))
    },
    [projectId, current, lockedFields, targetGroup, loadFilters, replaceUnlocked],
  )

  return { isTyped, parsed, apply }
}

interface TypedFilterItemsProps {
  parsed: NonNullable<ReturnType<typeof useTypedFilter>['parsed']>
  onApply: (filters: Array<TaskFilter>) => void
  heading: string
}

/**
 * "Due before Fri, Oct 2 ↵" plus any other reading ("Due on Fri, Oct 2"),
 * and a note for tokens that couldn't be read.
 */
export function TypedFilterItems({ parsed, onApply, heading }: TypedFilterItemsProps) {
  const { t } = useTranslation('tasks')
  const { describe } = useFilterLabels()
  const unreadable = parsed.tokens.filter((tok) => !tok.ok).map((tok) => tok.raw)

  // Each alternative swaps one token's reading, keeping the rest.
  const alternatives = parsed.tokens.flatMap((tok) =>
    tok.ok
      ? tok.alternatives.map((alt) =>
          parsed.filters.map((f) =>
            f.fieldKey === alt.fieldKey && f.group === alt.group ? alt : f,
          ),
        )
      : [],
  )

  const options = parsed.filters.length > 0 ? [parsed.filters, ...alternatives] : []

  return (
    <CommandGroup heading={heading}>
      {options.map((filters, i) => (
        <CommandItem
          key={`${TYPED_FILTER_ITEM_PREFIX}${i}`}
          value={`${TYPED_FILTER_ITEM_PREFIX}${i}`}
          onSelect={() => onApply(filters)}
          className="items-start gap-2 text-sm"
        >
          <TextCursorInput className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          {/* One line per chip, so long queries stay readable in a narrow menu. */}
          <span className="flex flex-1 flex-col gap-0.5 leading-snug">
            {filters.map((f, j) => (
              <span key={`${f.group ?? ''}-${f.fieldKey}`}>
                {j > 0 && f.group !== filters[j - 1]?.group && (
                  <span className="mr-1 text-[11px] font-semibold uppercase text-brand">
                    {t('filters.groups.or')}
                  </span>
                )}
                {describe(f)}
              </span>
            ))}
          </span>
          {i === 0 && <CornerDownLeft className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />}
        </CommandItem>
      ))}
      {options.length === 0 && (
        <div className="px-2 py-1.5 text-xs text-muted-foreground">{t('filters.typed.nothing')}</div>
      )}
      {unreadable.length > 0 && (
        <div className="px-2 py-1 text-xs text-muted-foreground">
          {t('filters.typed.unreadable', { tokens: unreadable.join(', ') })}
        </div>
      )}
    </CommandGroup>
  )
}
